// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TopMenu.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * TopMenu component
 */

  import { observable } from 'mobx';

  import React, { Component } from 'react';
  import classnames from 'classnames';
  import routeUtils from 'share/utils/Izenda.RouteUtils';
  import LayoutUtils from 'share/utils/Izenda.LayoutUtils';

  import connect from 'share/enhancers/Izenda.Connect';
  import uiState from 'logic/Izenda.UiState';
  import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
  import AccountStore from 'logic/Izenda.AccountStore';

  import IntegrationStyleAction from 'pages/Izenda.IntegrationStyleAction';
  const integrationStyleAction = IntegrationStyleAction.checkDisplayByKey('hideTopMenu');

  import { get, flow, isNil, partial, forEach, find, remove, placeholder, isEmpty } from 'lodash/fp';

  @connect(integrationStyleAction)
  class TopMenu extends Component {

    menuState: Object;
    accountStore = AccountStore;

    constructor(props: any) {
      super(props);
      this.accountStore = new AccountStore();
      this.menuState = observable({
        hasSetupDatabase: null
      });
    }

    componentDidMount() {
      this.accountStore
        .checkDatabaseSetup()
        .bind(this)
        .then(function(dbConfigured) {
          this.menuState.hasSetupDatabase = dbConfigured;
          return dbConfigured;
        });
    }
    showExpandedMenu = () => {
      LayoutUtils.toggleTopMenu();
    }

    renderListMenu = (item: any): any => {
      const { props } = this;
      if (item.childs) {
        return item.childs.map(this.renderListMenu);
      }
      const { to, className, title, icon } = item;
      function hideMenu() {
        LayoutUtils.toggleTopMenu(false);

        props.confirmDirtyForm(uiState.isDirty, { onConfirmDirtyForm: uiState.onOkDirtyForm }).then(function() {
          props.router.push(to);
        });
      }

      return (
        <a className={className} key={to} onClick={hideMenu}>
          <i className={icon} />
          <span className='izenda-TopMenu-title'>{title}</span>
        </a>
      );
    }

    getMenu = (localization: any): any => {
      const { props : {authorization, authorizationConstants } } = this;

      const permissionKeys = authorizationConstants.PERMISSION_KEY;
      const permissionOperator = authorizationConstants.PERMISSION_OPERATOR;
      const hasSetupDatabase = !!this.menuState.hasSetupDatabase;

      return [
        {
          childs: [
            {
              to: routeUtils.getReportUrl(),
              title: localization.getText('TOP_MENU_REPORTS'),
              icon: 'icon-report',
              needLicense: true,
              hasLicense: hasSetupDatabase && !uiState.expiredLicense
            },
            {
              to: routeUtils.getReportDesignerUrl(),
              icon: 'icon-addnew',
              needLicense: true,
              hasPrivilege:
                authorization.checkPrivileges([permissionKeys.ReportsCanCreateNewReportValue]) &&
                authorization.checkPrivileges(
                  [
                    permissionKeys.ReportsDataSourcesSimpleDataSources,
                    permissionKeys.ReportsDataSourcesAdvancedDataSources,
                    permissionKeys.ReportsReportPartTypesChart,
                    permissionKeys.ReportsReportPartTypesForm,
                    permissionKeys.ReportsReportPartTypesGauge,
                    permissionKeys.ReportsReportPartTypesMap,
                    permissionKeys.ReportsFilterPropertiesFilterLogic,
                    permissionKeys.ReportsFieldPropertiesCustomURL,
                    permissionKeys.ReportsFieldPropertiesEmbeddedJavaScript,
                    permissionKeys.ReportsFieldPropertiesSubreport,
                    permissionKeys.ReportsActionsSchedule,
                    permissionKeys.ReportsActionsConfigureAccessRights,
                    permissionKeys.ReportsActionsEmail,
                    permissionKeys.ReportsActionsPrint,
                    permissionKeys.ReportsActionsExporting
                  ],
                  permissionOperator.Or
                ),
              hasLicense:
                authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportDesigner) &&
                hasSetupDatabase &&
                !uiState.expiredLicense
            }
          ]
        },
        {
          childs: [
            {
              to: routeUtils.getDashboardUrl(),
              title: localization.getText('TOP_MENU_DASHBOARDS'),
              icon: 'icon-dashboard',
              needLicense: true,
              hasLicense:
                authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Dashboard) &&
                hasSetupDatabase &&
                !uiState.expiredLicense
            },
            {
              to: routeUtils.getDashboardUrl('New'),
              icon: 'icon-addnew',
              needLicense: true,
              isHidden: uiState.isMobile,
              hasPrivilege: authorization.checkPrivileges([permissionKeys.DashboardsCanCreateNewDashboardValue]),
              hasLicense:
                authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Dashboard) &&
                hasSetupDatabase &&
                !uiState.expiredLicense
            }
          ]
        },
        {
          to: routeUtils.getSettingsUrl(),
          title: localization.getText('TOP_MENU_SETTINGS'),
          icon: 'icon-setting',
          needLicense: false,
          isHidden: uiState.isMobile,
          hasPrivilege:
            !hasSetupDatabase ||
            authorization.checkPrivileges(
              [
                permissionKeys.TenantSetupActionsCreate,
                permissionKeys.TenantSetupActionsEdit,
                permissionKeys.TenantSetupActionsDel,
                permissionKeys.TenantSetupPermissionsValue,
                permissionKeys.SystemConfigurationScheduledInstancesValue,
                permissionKeys.DataSetupDataModelValue,
                permissionKeys.DataSetupAdvancedSettingsCategory,
                permissionKeys.DataSetupAdvancedSettingsOthers,
                permissionKeys.UserSetupActionsCreate,
                permissionKeys.UserSetupActionsEdit,
                permissionKeys.UserSetupActionsDel,
                permissionKeys.UserSetupActionsConfigureSecurityOptions,
                permissionKeys.UserSetupUserRoleAssociationValue,
                permissionKeys.RoleSetupActionsCreate,
                permissionKeys.RoleSetupActionsEdit,
                permissionKeys.RoleSetupActionsDel,
                permissionKeys.RoleSetupDataModelAccessValue,
                permissionKeys.RoleSetupPermissionsValue,
                permissionKeys.RoleSetupGrantRoleWithFullReportAndDashboardAccessValue
              ],
              permissionOperator.Or
            )
        }
      ];
    }

    render() {
      const { props : { router, localization, className } } = this;
      let links = this.getMenu(localization);
      let newLinks = [];

      const checkActiveRoute = flow(get('to'), router.isActive);

      const checkIndexActiveRoute = flow(get('to'), partial(router.isActive, [placeholder, true]));

      forEach(function(item) {
        if (item.childs) {
          const newItems = remove(function(child) {
            return (
              (!isNil(child.hasPrivilege) && !child.hasPrivilege) ||
              child.isHidden ||
              ((!isNil(child.hasLicense) && !child.hasLicense) || child.isHidden)
            );
          }, item.childs);

          if (!isEmpty(newItems)) {
            const newChild = { childs: newItems };
            const activeLink = find(checkActiveRoute, newItems);
            const indexActiveRoute = find(checkIndexActiveRoute, newItems);

            if (activeLink) {
              newChild.className = 'active';
              if (indexActiveRoute) {
                indexActiveRoute.className = 'active';
              } else {
                activeLink.className = 'active';
              }
            }

            newLinks.push(newChild);
          }
        } else {
          if (isNil(item.hasPrivilege) || (item.hasPrivilege && !item.isHidden)) {
            if (router.isActive(item.to)) {
              item.className = 'active';
            }

            newLinks.push(item);
          }
        }
      }, links);

      return (
        <div className={classnames('izenda-TopMenu', className)}>
          <div className='izenda-TopMenu-logo' />

          <nav className='izenda-TopMenu-nav' aria-expanded='false'>
            <ul className='nav'>
              {newLinks.map(
                (item, index) => {
                  return (
                    <li key={index} className={item.className}>
                      {this.renderListMenu(item)}
                    </li>
                  );
                }
              )}
            </ul>
          </nav>

          <div className='navbar-header'>
            <button type='button' className='navbar-toggle' onClick={this.showExpandedMenu}>
              <span className='icon-menu u-colorWhite' />
            </button>
          </div>
        </div>
      );
    }
  }
  export default TopMenu;




// WEBPACK FOOTER //
// ./ui/share/components/layout/Izenda.TopMenu.js